import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  ToggleButton,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { IAircraftGroup, IFlightType, IPosition } from "@vmosolutions/oc-interfaces";
import React from "react";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";
import {
  IPairingStatusFilters,
  NegatableFilterKey,
  StatusCounts,
  emptyPairingStatusFilters,
  hasActiveFilters,
  isNegated,
} from "./pairingStatusSelectors";
import {
  FLIGHT_PAIRING_STATUS_CATALOGUE,
  FLIGHT_PAIRING_STATUS_ORDER,
  FlightPairingStatusCode,
} from "./pairingStatusTypes";

interface IPairingStatusFiltersProps {
  filters: IPairingStatusFilters;
  onChange: (filters: IPairingStatusFilters) => void;
  /** Store the current filters and view on the user's profile as the defaults for next time. */
  onSave: () => void;
  /** False while the current filters already match the saved ones. */
  canSave: boolean;
  isSaving: boolean;
  /** Whether the profile holds a saved set at all (changes the wording). */
  hasSavedFilters: boolean;
  /** Counts per status for the rows that pass every filter except the status filter. */
  counts: StatusCounts;
  origins: string[];
  destinations: string[];
  /** Airport names by ICAO ident, when known, so the selects read "CYYZ - Toronto Pearson". */
  airportNames?: Record<string, string>;
  flightTypes: IFlightType[];
  aircraftGroups: IAircraftGroup[];
  positions: IPosition[];
}

const Bar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "0px 8px 4px 8px",
  // Each row is a fixed title column plus a wrapping area for the controls, so when the controls need a second
  // line it starts under the first control rather than under the title.
  "& .row": { display: "flex", flexDirection: "row", alignItems: "flex-start" },
  "& .row_title": { width: 44, flexShrink: 0, fontSize: 11, lineHeight: "26px", color: theme.custom.forms.labels },
  "& .row_fields": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 4,
    flexGrow: 1,
    minWidth: 0,
    minHeight: 26,
  },
  "& .status_chip": {
    height: 20,
    fontSize: 10,
    fontWeight: 700,
    color: theme.palette.common.black,
    border: `2px solid transparent`,
    "&.no_pairing": { backgroundColor: theme.custom.pairingStatus.noPairingColor },
    "&.missing_crew": { backgroundColor: theme.custom.pairingStatus.missingCrewColor },
    "&.extra_crew": { backgroundColor: theme.custom.pairingStatus.extraCrewColor },
    "&.extra_pos": { backgroundColor: theme.custom.pairingStatus.extraPosColor },
    "&.missing_ac_info": { backgroundColor: theme.custom.pairingStatus.missingACInfoColor },
    "&.ok": { backgroundColor: theme.custom.pairingStatus.okColor },
    "&.inactive": { opacity: 0.45 },
    "&.active": { borderColor: theme.palette.secondary.main },
    "&.excluded": { borderColor: theme.palette.error.main, textDecoration: "line-through" },
    "& .MuiChip-label": { paddingLeft: 6, paddingRight: 6 },
  },
  "& .filter_group": { display: "flex", flexDirection: "row", alignItems: "center", gap: 2 },
  "& .not_toggle": {
    height: 20,
    minWidth: 30,
    padding: "0px 5px",
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1,
    textTransform: "uppercase",
    "&.Mui-selected": {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.error.main,
      "&:hover": { backgroundColor: theme.palette.error.dark },
    },
  },
  // The form fields default to the full row width (one filter per line). Size them to their content instead so
  // several sit side by side and wrap to the next line only when the window is too narrow.
  "& .search_textfield": {
    width: "auto",
    margin: "2px 4px",
    "& .input_control label": { width: "auto", margin: "5px 6px auto 0px" },
    "& .input-base": { width: 170 },
  },
  "& .origin_selectfield, & .destination_selectfield, & .aircraftgroup_selectfield, & .flighttype_selectfield, & .position_selectfield":
    {
      width: "auto",
      margin: "2px 0px 2px 4px",
      "& .input_control label": { width: "auto", margin: "5px 6px auto 0px" },
      // Wide enough for a couple of selected values; longer selections wrap inside the control.
      "& .input_control .react-select-container": { flexGrow: 0, minWidth: 170, maxWidth: 360 },
    },
  "& .MuiFormControlLabel-root": { marginLeft: 0, marginRight: 8 },
  "& .MuiFormControlLabel-label": { fontSize: 11 },
  "& .clear_btn, & .save_btn": { padding: "0px 6px", height: 20, fontSize: 10 },
}));

/** "is / is not" switch for one value filter; red when the selected values are being excluded. */
const NotToggle = ({
  filters,
  filterKey,
  onChange,
  what,
}: {
  filters: IPairingStatusFilters;
  filterKey: NegatableFilterKey;
  onChange: (filters: IPairingStatusFilters) => void;
  what: string;
}): JSX.Element => {
  const negated = isNegated(filters, filterKey);
  return (
    <Tooltip
      title={
        negated
          ? `Showing flights whose ${what} is NOT one of the selected values - click to switch back to "is"`
          : `Click to exclude the selected ${what} values instead of keeping them`
      }
    >
      <ToggleButton
        className="not_toggle"
        value="not"
        size="small"
        selected={negated}
        aria-label={`${what} is not`}
        onChange={() => onChange({ ...filters, negate: { ...filters.negate, [filterKey]: !negated } })}
      >
        {negated ? "is not" : "is"}
      </ToggleButton>
    </Tooltip>
  );
};

/** Label for a select, suffixed when its values are being excluded. */
const labelFor = (filters: IPairingStatusFilters, filterKey: NegatableFilterKey, label: string): string =>
  isNegated(filters, filterKey) ? `${label} (not)` : label;

export const PairingStatusFilters = (props: IPairingStatusFiltersProps): JSX.Element => {
  const {
    filters,
    onChange,
    onSave,
    canSave,
    isSaving,
    hasSavedFilters,
    counts,
    origins,
    destinations,
    airportNames,
    flightTypes,
    aircraftGroups,
    positions,
  } = props;
  const update = (patch: Partial<IPairingStatusFilters>): void => onChange({ ...filters, ...patch });
  const airportLabel = (option: { ident: string }): string =>
    airportNames?.[option.ident] ? `${option.ident} - ${airportNames[option.ident]}` : option.ident;

  const statusesNegated = isNegated(filters, "statuses");
  const toggleStatus = (code: FlightPairingStatusCode): void => {
    const next = filters.statuses.includes(code)
      ? filters.statuses.filter((c) => c !== code)
      : [...filters.statuses, code];
    update({ statuses: next, problemsOnly: next.length > 0 ? false : filters.problemsOnly });
  };

  const total = FLIGHT_PAIRING_STATUS_ORDER.reduce((sum, code) => sum + (counts[code] ?? 0), 0);
  const problems = total - (counts.OK ?? 0);

  return (
    <Bar>
      <Box className="row">
        <Typography className="row_title">Status</Typography>
        <Box className="row_fields">
          <NotToggle filters={filters} filterKey="statuses" onChange={onChange} what="status" />
          {FLIGHT_PAIRING_STATUS_ORDER.map((code) => {
            const descriptor = FLIGHT_PAIRING_STATUS_CATALOGUE[code];
            const selected = filters.statuses.includes(code);
            // With "is": unselected chips dim once anything is selected. With "is not": selected chips are the
            // hidden ones, so they are struck through and everything else stays bright.
            const inactive =
              (filters.statuses.length > 0 && (statusesNegated ? selected : !selected)) ||
              (filters.problemsOnly && code === "OK");
            const className = `status_chip ${descriptor.className}${
              selected ? (statusesNegated ? " excluded" : " active") : ""
            }${inactive ? " inactive" : ""}`;
            return (
              <Tooltip
                key={code}
                title={`${descriptor.message} - click to ${statusesNegated ? "hide" : "show only"} this status`}
              >
                <Chip
                  className={className}
                  label={`${descriptor.label} ${counts[code] ?? 0}`}
                  size="small"
                  onClick={() => toggleStatus(code)}
                />
              </Tooltip>
            );
          })}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.problemsOnly}
                onChange={(_event, checked) =>
                  update({ problemsOnly: checked, statuses: checked ? [] : filters.statuses })
                }
              />
            }
            label={`Problems only (${problems})`}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.includeSubAircraft}
                onChange={(_event, checked) => update({ includeSubAircraft: checked })}
              />
            }
            label="Show sub / planning aircraft"
          />
          {hasActiveFilters(filters) && (
            <Button
              className="clear_btn"
              size="small"
              variant="outlined"
              startIcon={<ClearIcon fontSize="small" />}
              onClick={() => onChange({ ...emptyPairingStatusFilters, includeSubAircraft: filters.includeSubAircraft })}
            >
              Clear filters
            </Button>
          )}
          <Tooltip
            title={
              canSave
                ? "Save the current filters and view as your starting point for this window"
                : hasSavedFilters
                  ? "The current filters match your saved ones"
                  : "Change a filter, then save it as your starting point for this window"
            }
          >
            <span>
              <Button
                className="save_btn"
                id="save_flightpairingstatus_filters_btn"
                size="small"
                variant="outlined"
                disabled={!canSave || isSaving}
                startIcon={isSaving ? <CircularProgress size={10} color="inherit" /> : <SaveIcon fontSize="small" />}
                onClick={onSave}
              >
                {isSaving ? "Saving" : "Save filters"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box className="row">
        <Typography className="row_title">Filters</Typography>
        <Box className="row_fields">
          <Tooltip title="Flight number, route, registration or pairing code. Prefix a word with - to exclude it, e.g. -CYYZ">
            <span>
              <TextField
                className="search_textfield"
                name="pairing_status_search"
                label="Search"
                placeholder="Flight, route, reg, pairing, -exclude"
                value={filters.search}
                fullWidth={false}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  update({ search: event?.target?.value ?? "" })
                }
              />
            </span>
          </Tooltip>
          <Box className="filter_group">
            <SelectField
              className="origin_selectfield"
              name="pairing_status_origin"
              label={labelFor(filters, "origins", "Origin")}
              value={filters.origins.map((ident) => ({ ident }))}
              onChange={(selected: { ident: string }[]) => update({ origins: (selected ?? []).map((s) => s.ident) })}
              options={origins.map((ident) => ({ ident }))}
              getOptionLabel={airportLabel}
              getOptionValue={(option: { ident: string }) => option.ident}
              isSearchable={true}
              isClearable={true}
              isMulti={true}
            />
            <NotToggle filters={filters} filterKey="origins" onChange={onChange} what="origin" />
          </Box>
          <Box className="filter_group">
            <SelectField
              className="destination_selectfield"
              name="pairing_status_destination"
              label={labelFor(filters, "destinations", "Dest.")}
              value={filters.destinations.map((ident) => ({ ident }))}
              onChange={(selected: { ident: string }[]) =>
                update({ destinations: (selected ?? []).map((s) => s.ident) })
              }
              options={destinations.map((ident) => ({ ident }))}
              getOptionLabel={airportLabel}
              getOptionValue={(option: { ident: string }) => option.ident}
              isSearchable={true}
              isClearable={true}
              isMulti={true}
            />
            <NotToggle filters={filters} filterKey="destinations" onChange={onChange} what="destination" />
          </Box>
          <Box className="filter_group">
            <SelectField
              className="aircraftgroup_selectfield"
              name="pairing_status_aircraftgroup"
              label={labelFor(filters, "aircraftGroups", "AC Group")}
              value={filters.aircraftGroups}
              onChange={(selected: IAircraftGroup[]) => update({ aircraftGroups: selected ?? [] })}
              options={aircraftGroups}
              getOptionLabel={(option: IAircraftGroup) => `${option.title} (${option?.members?.length ?? 0})`}
              getOptionValue={(option: IAircraftGroup) => option._id}
              isSearchable={true}
              isClearable={true}
              isMulti={true}
            />
            <NotToggle filters={filters} filterKey="aircraftGroups" onChange={onChange} what="aircraft group" />
          </Box>
          <Box className="filter_group">
            <SelectField
              className="flighttype_selectfield"
              name="pairing_status_flighttype"
              label={labelFor(filters, "flightTypeIds", "Flight Type")}
              value={flightTypes.filter((ft) => filters.flightTypeIds.includes(ft._id))}
              onChange={(selected: IFlightType[]) => update({ flightTypeIds: (selected ?? []).map((ft) => ft._id) })}
              options={flightTypes}
              getOptionValue={(flightType: IFlightType) => flightType._id}
              getOptionLabel={(flightType: IFlightType) =>
                flightType ? `${flightType.title}${flightType.description ? ` - ${flightType.description}` : ""}` : ""
              }
              isSearchable={true}
              isClearable={true}
              isMulti={true}
            />
            <NotToggle filters={filters} filterKey="flightTypeIds" onChange={onChange} what="flight type" />
          </Box>
          <Box className="filter_group">
            <SelectField
              className="position_selectfield"
              name="pairing_status_position"
              label={labelFor(filters, "positions", "Positions")}
              value={positions.filter((p) => filters.positions.some((f) => f._id === p._id))}
              onChange={(selected: IPosition[]) =>
                update({ positions: (selected ?? []).map((p) => ({ _id: p._id, title: p.title })) })
              }
              options={positions}
              getOptionLabel={(option: IPosition) => option.title}
              getOptionValue={(option: IPosition) => option._id}
              isMulti={true}
              isClearable={true}
              isSearchable={true}
            />
            <NotToggle filters={filters} filterKey="positions" onChange={onChange} what="position" />
          </Box>
        </Box>
      </Box>
    </Bar>
  );
};
